import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import { Paginator } from '../../components/common/paginator';
import {
  FailedPipelineRun,
  useFailedPipelineRuns,
  useRunningPipelineRuns,
  useMarkRunReviewed,
  useRetryPipeline,
  useStartPipeline,
} from '../../hooks/useFailedPipelines';
import EditPipelineModal from './edit-pipeline-modal';
import BackgroundJobsTab from './background-jobs';
import JobControlsTab from './job-controls';

const PAGE_LIMIT = 25;

type PipelinesTab = 'audio' | 'jobs' | 'controls';

const tabs: { key: PipelinesTab; label: string }[] = [
  { key: 'audio', label: 'Audio Pipelines' },
  { key: 'jobs', label: 'Background Jobs' },
  { key: 'controls', label: 'Job Controls' },
];

const AudioPipelinesTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [editingRun, setEditingRun] = useState<FailedPipelineRun | null>(null);
  const [audioIdInput, setAudioIdInput] = useState('');

  const {
    data,
    isLoading,
    refetch: refetchFailed,
  } = useFailedPipelineRuns({
    page,
    limit: PAGE_LIMIT,
    include_reviewed: includeReviewed,
  });

  const { data: runningData, refetch: refetchRunning } = useRunningPipelineRuns();

  const handleRefresh = () => {
    refetchFailed();
    refetchRunning();
  };
  const { mutate: markReviewed, isLoading: isMarkingReviewed } = useMarkRunReviewed();
  const { mutate: retryPipeline, isLoading: isRetrying } = useRetryPipeline();
  const { mutate: startPipeline, isLoading: isStarting } = useStartPipeline();

  const runs = data?.data || [];
  const totalCount = data?.count || 0;
  const runningRuns = runningData?.data || [];

  const handleMarkReviewed = (runId: number) => {
    markReviewed({ runId, reviewed: true });
  };

  const handleUnmarkReviewed = (runId: number) => {
    markReviewed({ runId, reviewed: false });
  };

  const handleRetry = (runId: number) => {
    retryPipeline(runId);
  };

  const handleStartPipeline = () => {
    const id = parseInt(audioIdInput, 10);
    if (!id || isNaN(id)) return;
    startPipeline(id, { onSuccess: () => setAudioIdInput('') });
  };

  const formatDate = (dateStr: string) => {
    // Backend timestamps may be returned without a timezone (e.g. `2026-03-20T07:30:00`).
    // `new Date()` treats those as local time, which shifts the displayed time.
    // If there's no explicit timezone suffix, assume UTC.
    const normalized = /Z$|[+-]\d\d:\d\d$/.test(dateStr) ? dateStr : `${dateStr}Z`;
    return new Date(normalized).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getFailedStepNames = (run: FailedPipelineRun): string => {
    return (
      run.steps
        .filter((s) => s.status === 'failed')
        .map((s) => s.step_name)
        .join(', ') || 'Unknown'
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b px-7 py-3">
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeReviewed}
              onChange={(e) => {
                setIncludeReviewed(e.target.checked);
                setPage(1);
              }}
              className="rounded"
            />
            Show reviewed
          </label>
          <span className="text-sm text-gray-400">
            {totalCount} failed pipeline{totalCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={audioIdInput}
            onChange={(e) => setAudioIdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleStartPipeline()}
            placeholder="Audio ID"
            className="w-28 rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={handleStartPipeline}
            disabled={isStarting || !audioIdInput.trim()}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isStarting ? 'Starting...' : 'Run Pipeline'}
          </button>
          <button
            onClick={handleRefresh}
            title="Refresh"
            className="rounded border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      {runningRuns.length > 0 && (
        <div className="border-b bg-gray-50 px-7 py-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Running ({runningRuns.length})
          </h3>
          <div className="flex flex-wrap gap-3">
            {runningRuns.map((run) => {
              const completedSteps = run.steps.filter((s) => s.status === 'completed' || s.status === 'skipped').length;
              const totalSteps = run.steps.length;
              const currentStep = run.steps.find((s) => s.status === 'running' || s.status === 'queued');
              return (
                <div key={run.id} className="w-64 rounded-lg border border-blue-200 bg-white p-3 shadow-sm">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-mono text-xs text-gray-400">Run #{run.id}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                      {run.status}
                    </span>
                  </div>
                  <div className="mb-1 truncate text-sm font-medium">
                    {run.listener_name || `Audio #${run.listener_audio_id}`}
                  </div>
                  <div className="mb-2 text-xs text-gray-500">Audio ID: {run.listener_audio_id}</div>
                  <div className="mb-1 flex justify-between text-xs text-gray-500">
                    <span>{currentStep ? currentStep.step_name : 'queued'}</span>
                    <span>
                      {completedSteps}/{totalSteps} steps
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-blue-500 transition-all"
                      style={{ width: totalSteps > 0 ? `${(completedSteps / totalSteps) * 100}%` : '0%' }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-gray-400">Started {formatDate(run.created_at)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && <Spinner />}

      {!isLoading && runs.length === 0 && (
        <div className="flex items-center justify-center py-20 text-gray-400">No failed pipelines found</div>
      )}

      {!isLoading && runs.length > 0 && (
        <>
          <div className="max-window-height-table overflow-x-auto overflow-y-auto border-b px-7">
            <table className="table-row-format relative w-full table-fixed border-collapse">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <th className="w-16 py-3 pr-2">Run ID</th>
                  <th className="w-20 py-3 pr-2">Audio ID</th>
                  <th className="w-36 py-3 pr-2">Listener</th>
                  <th className="w-32 py-3 pr-2">Failed Step</th>
                  <th className="py-3 pr-2">Error</th>
                  <th className="w-40 py-3 pr-2">Created</th>
                  <th className="w-56 py-3 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    className={`border-b text-sm hover:bg-gray-50 ${run.reviewed ? 'bg-gray-50 opacity-60' : ''}`}
                  >
                    <td className="py-3 pr-2 font-mono text-xs">{run.id}</td>
                    <td className="py-3 pr-2 font-mono text-xs">{run.listener_audio_id}</td>
                    <td className="truncate py-3 pr-2">
                      <div className="truncate font-medium">{run.listener_name || '—'}</div>
                      {run.listener_role_id && <div className="text-xs text-gray-400">Role {run.listener_role_id}</div>}
                    </td>
                    <td className="py-3 pr-2">
                      <span className="inline-block rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        {getFailedStepNames(run)}
                      </span>
                    </td>
                    <td className="py-3 pr-2">
                      <div className="max-h-12 overflow-hidden text-xs text-gray-600" title={run.error_message || ''}>
                        {run.error_message
                          ? run.error_message.length > 120
                            ? run.error_message.substring(0, 120) + '...'
                            : run.error_message
                          : '—'}
                      </div>
                      {run.steps
                        .filter((s) => s.status === 'failed')
                        .map((step) =>
                          step.error_traceback ? (
                            <details key={step.id} className="mt-1 text-xs text-gray-500">
                              <summary className="cursor-pointer font-medium text-gray-700">
                                {step.step_name} traceback
                              </summary>
                              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-2 text-[10px]">
                                {step.error_traceback}
                              </pre>
                            </details>
                          ) : step.error_message ? (
                            <details key={step.id} className="mt-1 text-xs text-gray-500">
                              <summary className="cursor-pointer font-medium text-gray-700">
                                {step.step_name} error
                              </summary>
                              <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-100 p-2 text-[10px]">
                                {step.error_message}
                              </pre>
                            </details>
                          ) : null,
                        )}
                    </td>
                    <td className="py-3 pr-2 text-xs text-gray-500">{formatDate(run.created_at)}</td>
                    <td className="py-3 pr-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditingRun(run)}
                          className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRetry(run.id)}
                          disabled={isRetrying}
                          className="rounded px-2 py-1 text-xs font-medium text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                        >
                          Retry
                        </button>
                        {run.reviewed ? (
                          <button
                            onClick={() => handleUnmarkReviewed(run.id)}
                            disabled={isMarkingReviewed}
                            className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                          >
                            Unmark
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMarkReviewed(run.id)}
                            disabled={isMarkingReviewed}
                            className="rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                          >
                            Mark Seen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginator count={totalCount} limit={PAGE_LIMIT} currentPage={page} handlePageChange={setPage} />
        </>
      )}

      {editingRun && <EditPipelineModal run={editingRun} onClose={() => setEditingRun(null)} />}
    </div>
  );
};

const parseTabFromSearch = (search: string): PipelinesTab => {
  const tab = new URLSearchParams(search).get('tab');
  if (tab === 'jobs') return 'jobs';
  if (tab === 'controls') return 'controls';
  return 'audio';
};

const PipelinesPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<PipelinesTab>(() => parseTabFromSearch(location.search));

  useEffect(() => {
    setActiveTab(parseTabFromSearch(location.search));
  }, [location.search]);

  const selectTab = useCallback(
    (tab: PipelinesTab) => {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      if (tab === 'audio') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      const search = params.toString();
      history.replace({ ...location, search: search ? `?${search}` : '' });
    },
    [history, location],
  );

  return (
    <div>
      <Header heading="Pipelines" />
      <div className="flex border-b border-gray-200 px-7">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`mr-4 border-b-2 px-4 py-3 text-sm font-semibold ${
                isActive ? 'border-blue-primary text-blue-primary' : 'text-gray-dark border-transparent'
              }`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'audio' && <AudioPipelinesTab />}
      {activeTab === 'jobs' && <BackgroundJobsTab />}
      {activeTab === 'controls' && <JobControlsTab />}
    </div>
  );
};

export default PipelinesPage;
